import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type IAccountRepository } from '@webapp/platform/finance/business-logic/interfaces/IAccountRepository'
import { type AccountConstructor } from '@webapp/platform/finance/entities/Account'
import { mapAccountsApiModel } from '../../mappers/mapAccountsApiModel'
import { type GetAccountByIdResponse } from '../../types/GetAccountByIdResponse'
import { type GetRequestableAccountsResponse } from '../../types/GetRequestableAccountsResponse'

interface Dependency {
  httpClient: IHttpClient
}

export class AccountRepository implements IAccountRepository {
  constructor(private readonly deps: Dependency) {}

  async list(
    params: {
      secondaryCategoryId?: number
      active?: boolean
    } & RepoFilterParams = {}
  ): Promise<RepoPaginatedResponse<AccountConstructor>> {
    const { secondaryCategoryId, active = true, ...filterParams } = params

    try {
      const res =
        await this.deps.httpClient.get<GetRequestableAccountsResponse>(
          `/api/v4/accounts/`,
          {
            params: {
              department: secondaryCategoryId,
              active,
              ...formatRepoFiltersToApiFilters(filterParams),
            },
          }
        )

      const accounts = res.data.data
      const metadata = res.data.metadata

      return {
        data: accounts.map((account) => mapAccountsApiModel(account)),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getRequestableAccountsBySecondaryCategory(
    secondaryCategoryId: number,
    params?: RepoFilterParams
  ): Promise<RepoPaginatedResponse<AccountConstructor>> {
    try {
      const res =
        await this.deps.httpClient.get<GetRequestableAccountsResponse>(
          `/api/v3/accounts/requestable/`,
          {
            params: {
              department: secondaryCategoryId,
              ...formatRepoFiltersToApiFilters(params),
            },
          }
        )

      const accounts = res.data.data
      const metadata = res.data.metadata

      return {
        data: accounts.map((account) => mapAccountsApiModel(account)),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getAccountById(accountId: number): Promise<AccountConstructor | null> {
    try {
      const res = await this.deps.httpClient.get<GetAccountByIdResponse>(
        `/api/v3/accounts/${accountId}/`
      )

      return res.data.data ? mapAccountsApiModel(res.data.data) : null
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}
